import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Zap } from "lucide-react";

/**
 * Performance monitoring component for development
 * Shows FPS, memory usage, and render times
 */
export function PerformanceMonitor() {
  const [fps, setFps] = useState(0);
  const [memory, setMemory] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show in development
    if (import.meta.env.PROD) return;

    let frameCount = 0;
    let lastTime = performance.now();
    let animationId: number;

    const updateFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      const elapsed = currentTime - lastTime;

      if (elapsed >= 1000) {
        setFps(Math.round((frameCount * 1000) / elapsed));
        frameCount = 0;
        lastTime = currentTime;

        // Update memory if available
        if ((performance as any).memory) {
          const memoryUsage = (performance as any).memory.usedJSHeapSize / 1048576;
          setMemory(Math.round(memoryUsage));
        }
      }

      animationId = requestAnimationFrame(updateFPS);
    };

    animationId = requestAnimationFrame(updateFPS);

    // Toggle with Ctrl+Shift+P
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        setVisible(v => !v);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  if (!visible || import.meta.env.PROD) return null;

  return (
    <Card className="fixed bottom-4 right-4 p-3 bg-background/95 backdrop-blur z-50 shadow-lg">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4" />
          <span className="text-xs font-medium">Performance</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Zap className="h-3 w-3 text-primary" />
          <span className="text-xs">FPS:</span>
          <Badge variant={fps >= 55 ? "default" : fps >= 30 ? "outline" : "destructive"}>
            {fps}
          </Badge>
        </div>
        
        {memory > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs">Memory:</span>
            <Badge variant={memory < 100 ? "default" : "destructive"}>
              {memory} MB
            </Badge>
          </div>
        )}
        
        <p className="text-xs text-muted-foreground">
          Press Ctrl+Shift+P to toggle
        </p>
      </div>
    </Card>
  );
}

/**
 * Log performance metrics for a component render
 */
export function usePerformanceLog(componentName: string) {
  useEffect(() => {
    if (import.meta.env.PROD) return;
    
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      
      if (renderTime > 16) { // Slower than 60fps
        console.warn(`[Performance] ${componentName} render took ${renderTime.toFixed(2)}ms`);
      }
    };
  });
}
